/**
 * Gen1 power monitoring capabilities
 * Used by Gen1 devices with PM (power metering)
 *
 * Gen1 power data is in meters[channel].power path
 */

/**
 * Gen1 power level condition
 * @param {number} maxPower - Maximum power capacity in watts
 */
export function createGen1PowerLevelCondition(maxPower = 3500) {
	return {
		name: 'Power Level',
		description: 'Check if current power consumption meets a condition',
		parameters: {
			operator: {
				type: 'enum',
				required: true,
				values: ['greater_than', 'less_than', 'equal_to', 'between'],
				default: 'greater_than',
				description: 'Comparison operator',
			},
			threshold: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Power threshold value',
			},
			threshold_max: {
				type: 'number',
				required: false,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Upper threshold (for "between" operator)',
			},
		},
		evaluateCondition: (device, channel, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const currentPower = device.status?.meters?.[channelId]?.power || 0;
			const { operator, threshold, threshold_max } = params;

			switch (operator) {
				case 'greater_than':
					return currentPower > threshold;
				case 'less_than':
					return currentPower < threshold;
				case 'equal_to':
					return Math.abs(currentPower - threshold) < 1;
				case 'between':
					return currentPower >= threshold && currentPower <= (threshold_max || maxPower);
				default:
					return false;
			}
		},
	};
}

/**
 * Gen1 power threshold trigger
 * @param {number} maxPower - Maximum power capacity in watts
 */
export function createGen1PowerThresholdTrigger(maxPower = 3500) {
	return {
		name: 'Power Threshold',
		description: 'Fires when power consumption crosses a threshold',
		parameters: {
			threshold: {
				type: 'number',
				required: true,
				min: 0,
				max: maxPower,
				unit: 'watts',
				description: 'Power threshold value',
			},
			direction: {
				type: 'enum',
				required: true,
				values: ['above', 'below', 'either'],
				default: 'above',
				description: 'Trigger when power goes above or below threshold',
			},
		},
		evaluateTrigger: (device, channel, previousStatus, currentStatus, params) => {
			const channelId = parseInt(channel.split(':')[1]) || 0;
			const prevPower = previousStatus?.meters?.[channelId]?.power || 0;
			const currPower = currentStatus?.meters?.[channelId]?.power || 0;
			const { threshold, direction } = params;

			const wasBelow = prevPower < threshold;
			const isAbove = currPower >= threshold;

			if (direction === 'above') {
				return wasBelow && isAbove;
			} else if (direction === 'below') {
				return !wasBelow && !isAbove;
			} else {
				return (wasBelow && isAbove) || (!wasBelow && !isAbove);
			}
		},
	};
}
